import {
  publishWorkflowTemplateVersion,
  updateWorkflowTemplateVersion,
} from '@commenda/api';
import { Button, Icons, Tooltip, useToggle } from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import {
  IWorkflowDefinitionValidationResponse,
  IWorkflowTemplateVersion,
  TWorkflowDefinition,
} from '@commenda/types';
import { copyToClipboard, messageFromApiErrorResponse } from '@commenda/utils';
import { WorkflowTemplateVersionStatus } from '@prisma/client';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { ImportFromJsonModal } from './modals/ImportFromJsonModal';
import { ValidationErrorsModal } from './modals/ValidationErrorsModal';
import { PublishWorkflowPopover } from './PublishWorkflowPopover';
import { useWorkflowVersionActivation } from './useWorkflowVersionActivation';
import { validateRedirectNodes } from './validation';
import { WorkflowTemplateVersionStatusBadge } from './WorkflowTemplateVersionStatusBadge';

export const WorkflowEditorToolbar = ({
  workflowTemplateVersion,
  workflowDefinition,
  setWorkflowDefinition,
  handlers,
  hasUnsavedChanges,
  markAsSaved,
  onVersionChanged,
  backHref,
  onCreateLink,
}: {
  workflowTemplateVersion: IWorkflowTemplateVersion;
  workflowDefinition: TWorkflowDefinition;
  setWorkflowDefinition: (definition: TWorkflowDefinition) => void;
  handlers?: {
    onSave?: () => void;
    onPublish?: () => void;
    onUndo?: () => void;
    onRedo?: () => void;
    onAddGroup?: () => void;
    onAutoLayout?: () => void;
  };
  hasUnsavedChanges?: () => boolean;
  markAsSaved?: () => void;
  /** Fires after a publish or a make-live succeeds, so the host re-reads the version it holds. */
  onVersionChanged?: () => void;
  /** Where the back chevron lands. Default keeps the client app's /workflow-management route. */
  backHref?: string;
  /** Opens the host's link flow from the publish popover. Absent hides the entry: only the admin
   *  app has workflow links. */
  onCreateLink?: () => void;
}) => {
  const router = useRouter();
  const { setNotification } = useNotification();
  const activation = useWorkflowVersionActivation(
    workflowTemplateVersion,
    onVersionChanged
  );

  const isSaving = useToggle(false);
  const isPublishing = useToggle(false);

  const importJsonModalVisibility = useToggle(false);
  const validationErrorsModalVisibility = useToggle(false);

  const [validationResponse, setValidationResponse] =
    useState<IWorkflowDefinitionValidationResponse | null>(null);

  const onSave = async () => {
    isSaving.on();
    const res = await updateWorkflowTemplateVersion(
      workflowTemplateVersion.id,
      {
        definition: workflowDefinition,
      }
    );
    if (!res.ok) {
      setNotification({
        title: 'Error',
        message: await messageFromApiErrorResponse(
          res,
          'Failed to save workflow template version'
        ),
        show: true,
        valid: false,
      });
      isSaving.off();
      return;
    }
    markAsSaved?.();
    setNotification({
      title: 'Success',
      message: 'Workflow template version saved successfully',
      show: true,
      valid: true,
    });
    isSaving.off();
  };

  /** Resolves true only when the version actually published, so a caller can decide what comes next
   *  (the publish popover advances to its second step on true and holds still on false). */
  const onPublish = async (): Promise<boolean> => {
    if (hasUnsavedChanges?.()) {
      setNotification({
        title: 'Unsaved changes',
        message: 'Please save your workflow before publishing.',
        show: true,
        valid: false,
      });
      return false;
    }

    const clientErrors = validateRedirectNodes(workflowDefinition);
    if (clientErrors.length > 0) {
      setValidationResponse({
        message:
          'Workflow has configuration errors that must be resolved before publishing.',
        details: { errors: clientErrors, warnings: [] },
      });
      validationErrorsModalVisibility.on();
      return false;
    }

    isPublishing.on();
    const res = await publishWorkflowTemplateVersion(
      workflowTemplateVersion.id
    );
    if (!res.ok) {
      try {
        const errorData: IWorkflowDefinitionValidationResponse =
          await res.json();
        // Check if the error response contains validation errors/warnings
        if (
          errorData &&
          errorData.details &&
          errorData.details.errors &&
          Array.isArray(errorData.details.errors)
        ) {
          setValidationResponse(errorData);
          validationErrorsModalVisibility.on();
        } else {
          setNotification({
            title: 'Error',
            message: 'Failed to publish workflow template version',
            show: true,
            valid: false,
          });
        }
      } catch (parseError) {
        setValidationResponse(null);
        setNotification({
          title: 'Error',
          message: 'Failed to publish workflow template version',
          show: true,
          valid: false,
        });
      }
      isPublishing.off();
      return false;
    }
    // Clear validation errors on successful publish
    setValidationResponse(null);
    setNotification({
      title: 'Success',
      message: 'Workflow template version published successfully',
      show: true,
      valid: true,
    });
    try {
      await mixpanel.track(MIXPANEL_EVENTS.WORKFLOW_PUBLISHED, {
        surface_area: MIXPANEL_SURFACE_AREA.WORKFLOW_AND_FORM_BUILDER,
        workflow_template_id: workflowTemplateVersion?.workflowTemplate?.id,
        version: workflowTemplateVersion?.version,
      });
    } catch (error) {
      console.error('Error tracking workflow published event:', error);
    }
    isPublishing.off();
    await activation.refreshTemplate();
    onVersionChanged?.();

    return true;
  };

  const onCopyWorkflow = () => {
    copyToClipboard(JSON.stringify(workflowDefinition));
    setNotification({
      title: 'Success',
      message: 'Workflow copied to clipboard',
      show: true,
      valid: true,
    });
  };

  return (
    <div className="p-2 flex justify-between items-center">
      <div className="flex gap-2 items-center">
        <Button
          variant="tertiary"
          onClick={() =>
            router.push(
              backHref ??
                `/workflow-management/${workflowTemplateVersion?.workflowTemplate?.id}`
            )
          }
        >
          <Icons.ChevronLeft />
        </Button>
        <div className="flex flex-col">
          <h3 className="txt-medium-bold">
            {workflowTemplateVersion?.workflowTemplate?.name}
          </h3>
          <span className="txt-extra-small-regular">
            {workflowTemplateVersion?.version}
          </span>
        </div>
        <WorkflowTemplateVersionStatusBadge
          status={workflowTemplateVersion?.status}
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <Tooltip content="Copy Workflow">
          <Button
            variant="tertiary"
            size="sm"
            onClick={onCopyWorkflow}
            disabled={isSaving.value}
            isLoading={isSaving.value}
          >
            <Icons.Copy01 className="size-5" />
          </Button>
        </Tooltip>
        {workflowTemplateVersion.status ===
          WorkflowTemplateVersionStatus.DRAFT && (
          <>
            {/* {handlers?.onUndo && (
            <Button variant="tertiary" size="sm" onClick={handlers.onUndo}>
              <Icons.ReverseLeft className="size-5" />
            </Button>
          )}
          {handlers?.onRedo && (
            <Button variant="tertiary" size="sm" onClick={handlers.onRedo}>
              <Icons.ReverseRight className="size-5" />
            </Button>
          )} */}

            <Tooltip content="Auto layout">
              <Button
                variant="tertiary"
                size="sm"
                onClick={handlers?.onAutoLayout}
              >
                <Icons.Grid01 className="size-5" />
              </Button>
            </Tooltip>

            <Tooltip content="Import from JSON">
              <Button
                variant="tertiary"
                size="sm"
                onClick={importJsonModalVisibility.on}
              >
                <Icons.ArrowDown className="size-5" />
              </Button>
            </Tooltip>

            <Button
              variant="tertiary"
              size="sm"
              onClick={onSave}
              disabled={isSaving.value}
              isLoading={isSaving.value}
            >
              <Icons.Save02 className="size-5" />
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={handlers?.onAddGroup}
            >
              <Icons.Plus className="size-5" />
              Groups
            </Button>
            {validationResponse &&
              (validationResponse.details.errors.length > 0 ||
                validationResponse.details.warnings.length > 0) && (
                <Tooltip content="View validation errors and warnings">
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={validationErrorsModalVisibility.on}
                    className="txt-medium-bold text-text-error"
                  >
                    <span>Issues found</span>
                    {validationResponse.details.errors.length > 0 && (
                      <span>({validationResponse.details.errors.length})</span>
                    )}
                  </Button>
                </Tooltip>
              )}
          </>
        )}
        {/* Outside the draft gate on purpose. The popover's second step exists to offer going live,
            and a version stops being a draft the moment it publishes, so gating it on DRAFT would
            make the button vanish exactly when step two became relevant. */}
        <PublishWorkflowPopover
          versionLabel={`Version ${workflowTemplateVersion.version}`}
          lastSavedAt={
            workflowTemplateVersion.updatedAt
              ? String(workflowTemplateVersion.updatedAt)
              : undefined
          }
          isPublished={
            workflowTemplateVersion.status !==
            WorkflowTemplateVersionStatus.DRAFT
          }
          isActive={activation.isActive}
          isPublishing={isPublishing.value}
          isActivating={activation.isActivating}
          onCreateLink={onCreateLink}
          onPublish={onPublish}
          onActivate={activation.activate}
          activationWarnings={activation.activationWarnings}
          activationReadState={activation.activationReadState}
        />
      </div>
      <ImportFromJsonModal
        isOpen={importJsonModalVisibility.visible}
        onClose={importJsonModalVisibility.hide}
        setWorkflowDefinition={setWorkflowDefinition}
      />
      <ValidationErrorsModal
        isOpen={validationErrorsModalVisibility.visible}
        onClose={validationErrorsModalVisibility.hide}
        errors={validationResponse?.details.errors || []}
        warnings={validationResponse?.details.warnings || []}
      />
    </div>
  );
};
